import PropTypes from 'prop-types';
import React, { useState } from 'react';
import clsx from 'clsx';
import './page-header.scss';
import { Search, X } from 'react-feather';
import Button, { ButtonTypes } from '../button';
import { IconColors, IconSizes } from '../icon';
import Tooltip from '../tooltip';
import useMobileView from '../../enhancers/custom-hook/use-mobile-view';
import SearchInput from '../search-input';
import Badge, { BadgeTypes } from '../badge';
import { getRenderIcon } from '../../../utils/general-utils';

const PageHeaderOverrides = {
  MOBILE_BADGE: 'mobile-badge',
  // makes header badge smaller in drawer in mobile view
};

const PageHeader = ({
  text, supportingText, headerBadgeText, buttons, showSearch, searchPlaceholder, onButtonClick,
  searchText, onSearchApply, actionIcons, onAction, className, supportingTextIcons, onSearchChange,
  headerBadgeType, renderHeaderBadgeIcon, overrides,
}) => {
  const isMobileView = useMobileView();
  const [mobileSearchBarExpanded, setMobileSearchBarExpanded] = useState(false);

  const getActionIcons = () => {
    if (actionIcons.length) {
      return (
        <div className="page-header-action-icons">
          {actionIcons.map(({
            id, render: renderActionIcon, color, description,
          }) => (
            <Tooltip description={description} key={id}>
              <span className="page-header-action-icon" onClick={() => onAction(id)}>
                {renderActionIcon({ size: IconSizes.MEDIUM, color, type: renderActionIcon })}
              </span>
            </Tooltip>
          ))}
        </div>
      );
    }
    return null;
  };

  const getButton = ({
    id, label, type, disabled, renderIcon, title, buttonClassName, borderLess, testIds,
  }) => (
    <Button
      label={isMobileView ? '' : label}
      title={title}
      renderIcon={renderIcon}
      type={type}
      onClick={() => onButtonClick(id)}
      key={id}
      disabled={disabled}
      className={buttonClassName}
      borderLess={borderLess}
      testIds={testIds}
    />
  );

  const getMobileButtons = () => {
    const newButtons = buttons.filter((btn) => !btn.isCreateAction).map((ele) => ({
      ...ele,
      label: (ele.description ? ele.description : ele.label),
    }));

    if (!newButtons.length) return null;

    return newButtons.map(({
      renderIcon, id, buttonClassName, type, disabled,
    }) => (
      <span
        key={id}
        className={clsx('pht-button', buttonClassName)}
        onClick={() => {
          if (!disabled) {
            onButtonClick(id);
          }
        }}
      >
        {renderIcon({
          size: IconSizes.MEDIUM, type, disabled,
        })}
      </span>
    ));
  };

  const getButtons = () => {
    if (buttons.length) {
      const buttonViews = buttons.map((buttonData) => (
        buttonData.description
          ? (
            <Tooltip description={buttonData.description} key={buttonData.id}>
              {getButton(buttonData)}
            </Tooltip>
          ) : getButton(buttonData)
      ));

      return (
        <div className="page-header-buttons">
          {buttonViews}
        </div>
      );
    }

    return null;
  };

  const getSearchInput = () => {
    if (showSearch) {
      return (
        <SearchInput
          value={searchText}
          onApply={onSearchApply}
          onChange={onSearchChange}
          className="page-header-search"
          placeholder={searchPlaceholder}
        />
      );
    }
    return null;
  };

  const getMobileSearchBar = () => {
    if (!showSearch) return null;
    if (mobileSearchBarExpanded) {
      return (
        <div className="phm-search-bar">
          <Button
            borderLess
            renderIcon={getRenderIcon(X)}
            type={ButtonTypes.SECONDARY}
            className="phm-search-close-button"
            onClick={() => {
              onSearchChange({ target: { value: '' } });
              setMobileSearchBarExpanded(false);
              onSearchApply('');
            }}
          />
          {getSearchInput()}
        </div>
      );
    }
    return (
      <Button
        borderLess
        renderIcon={getRenderIcon(Search)}
        type={ButtonTypes.SECONDARY}
        className="phm-search-button"
        onClick={() => setMobileSearchBarExpanded(true)}
      />
    );
  };

  const getHeaderBadge = () => {
    const getSize = () => {
      if (isMobileView && overrides[PageHeaderOverrides.MOBILE_BADGE]) {
        return IconSizes.SMALL;
      }
      return IconSizes.MEDIUM;
    };
    if (headerBadgeText) {
      return (
        <div className="page-header-badge">
          <Badge
            label={headerBadgeText}
            type={headerBadgeType}
            renderIcon={renderHeaderBadgeIcon}
            size={getSize()}
          />
        </div>
      );
    }
    return null;
  };

  return (
    <div className={clsx('page-header-cont', className)}>
      <div className="page-header-texts">
        <div className="page-header-main-text-icons">
          <div className="page-header-text">{text}</div>
          {getHeaderBadge()}
          {getActionIcons()}
        </div>
        <div className="page-header-sup-text-and-icons">
          <div className="page-header-sup-text">{supportingText}</div>
          <div className="page-header-sup-text-icons">{supportingTextIcons}</div>
        </div>
      </div>
      <div className="page-header-tools">
        {isMobileView ? getMobileButtons() : getButtons()}
        {isMobileView ? getMobileSearchBar() : getSearchInput()}
      </div>
    </div>
  );
};

PageHeader.defaultProps = {
  supportingText: '',
  headerBadgeText: '',
  buttons: [],
  actionIcons: [],
  showSearch: false,
  searchPlaceholder: 'Search',
  searchText: '',
  className: '',
  onSearchApply: () => {},
  onButtonClick: () => {},
  onSearchChange: () => {},
  onAction: () => {},
  supportingTextIcons: [],
  headerBadgeType: BadgeTypes.PRIMARY,
  renderHeaderBadgeIcon: null,
  overrides: {},
};

PageHeader.propTypes = {
  text: PropTypes.string.isRequired,
  supportingText: PropTypes.string,
  headerBadgeText: PropTypes.string,
  buttons: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
    label: PropTypes.string,
    title: PropTypes.string,
    buttonClassName: PropTypes.string,
    type: PropTypes.oneOf(Object.values(ButtonTypes)),
    renderIcon: PropTypes.func,
    disabled: PropTypes.bool,
    isCreateAction: PropTypes.bool,
  })),
  actionIcons: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
    renderIcon: PropTypes.func,
    color: PropTypes.oneOf(Object.values(IconColors)),
  })),
  onAction: PropTypes.func,
  showSearch: PropTypes.bool,
  searchPlaceholder: PropTypes.string,
  searchText: PropTypes.string,
  onSearchApply: PropTypes.func,
  onButtonClick: PropTypes.func,
  onSearchChange: PropTypes.func,
  className: PropTypes.string,
  supportingTextIcons: PropTypes.node,
  headerBadgeType: PropTypes.oneOf(Object.values(BadgeTypes)),
  renderHeaderBadgeIcon: PropTypes.oneOfType([PropTypes.element,
    PropTypes.elementType, PropTypes.func, PropTypes.oneOf([null])]),
  overrides: PropTypes.shape(),
};

export {
  PageHeader as default,
  PageHeaderOverrides,
};
